import { Node, mergeAttributes } from "@tiptap/core";
import { t } from "../../lib/i18n";

const VIMEO_HOSTS = ["vimeo.com", "www.vimeo.com", "player.vimeo.com"];
const PLAYER_ORIGIN = "https://player.vimeo.com";
const ALLOWED_PLAYER_PARAMS = [
  "h",
  "autoplay",
  "badge",
  "byline",
  "color",
  "dnt",
  "loop",
  "muted",
  "portrait",
  "title",
];

function parsedUrl(value) {
  if (typeof value !== "string" || value.trim() === "") return null;
  try {
    return new URL(value.trim());
  } catch (_error) {
    return null;
  }
}

function videoIdIndex(segments) {
  const videoIndex = segments.indexOf("video");
  if (videoIndex !== -1) return videoIndex + 1;

  return segments.findIndex((segment) => /^\d+$/.test(segment));
}

function videoIdAndHash(url) {
  const segments = url.pathname.split("/").filter((segment) => segment !== "");
  const idIndex = videoIdIndex(segments);
  const id = segments[idIndex];
  if (!/^\d+$/.test(id || "")) return null;

  const hash = segments[idIndex + 1];
  return {
    id: id,
    hash: /^[A-Za-z0-9]+$/.test(hash || "") ? hash : null,
  };
}

// Rebuilds any Vimeo URL as a player embed URL, dropping every attribute the
// document may carry that we do not want to hand to an iframe. Returns null for
// anything that is not a Vimeo video, so stored content cannot smuggle in an
// arbitrary frame source.
export function vimeoEmbedSrc(value) {
  const url = parsedUrl(value);
  if (!url) return null;
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (!VIMEO_HOSTS.includes(url.hostname.toLowerCase())) return null;

  const video = videoIdAndHash(url);
  if (!video) return null;

  const embed = new URL(`${PLAYER_ORIGIN}/video/${video.id}`);
  ALLOWED_PLAYER_PARAMS.forEach((param) => {
    const param_value = url.searchParams.get(param);
    if (param_value !== null) embed.searchParams.set(param, param_value);
  });
  if (video.hash && !embed.searchParams.has("h")) {
    embed.searchParams.set("h", video.hash);
  }
  return embed.toString();
}

export const Vimeo = Node.create({
  name: "vimeo",
  group: "block",
  atom: true,
  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: { class: "tiptap-vimeo" },
    };
  },

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-vimeo-src"),
        renderHTML: (attributes) => ({ "data-vimeo-src": attributes.src }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-vimeo-src]",
        getAttrs: (element) => {
          const src = vimeoEmbedSrc(element.getAttribute("data-vimeo-src"));
          return src ? { src } : false;
        },
      },
      {
        tag: "iframe[src]",
        getAttrs: (element) => {
          const src = vimeoEmbedSrc(element.getAttribute("src"));
          return src ? { src } : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const src = HTMLAttributes["data-vimeo-src"];

    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      [
        "iframe",
        {
          src: src,
          title: t("views.shared.tiptap.vimeo.iframe_title", "Vimeo video"),
          frameborder: "0",
          allow: "autoplay; fullscreen; picture-in-picture",
          allowfullscreen: "true",
          referrerpolicy: "strict-origin-when-cross-origin",
        },
      ],
    ];
  },

  addCommands() {
    return {
      setVimeoVideo: (options) => ({ commands }) => {
        const src = vimeoEmbedSrc(options && options.src);
        if (!src) return false;

        return commands.insertContent({ type: this.name, attrs: { src: src } });
      },
    };
  },
});

export default Vimeo;
